import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React from 'react';

import BlockSection from '~/components/wrappers/BlockSection';
import IllustrationsBackground from '~/components/wrappers/IllustrationsBackground';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import Image from '~/components/base/Image';
import BaseLink from '~/components/base/BaseLink';
import Icon from '~/components/base/Icon';

import { formatText } from '~/utilities/helpers';
import { h3, h5, p } from '~/styles/typography';
import { setSmallSpacing } from '~/utilities/spacing';
import { transition } from '~/styles/transition';

import {
    GlobalBlockOptions,
    AcfAttributes,
    DefaultGlobalBlockOptions,
    DefaultAcfAttributes
} from '~/utilities/propTypes';

const FeaturedImageWrapper = styled.div`
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.text.lightBlue};
    position: relative;
    width: 100%;
    max-width: 637px;
    aspect-ratio: 1;
`;

const FeaturedImage = styled(Image)`
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    aspect-ratio: 1;
    border-radius: 50%;
`;

const Subtitle = styled.div`
    ${transition({}, 'color')};
    ${p};
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.black};
    color: ${({ theme }) => theme.colors.primary.yellow};
    text-transform: uppercase;
    ${setSmallSpacing('margin-bottom')};
`;

const Title = styled.h1`
    ${transition({}, 'color')};
    ${h3};
    color: ${({ theme }) => theme.colors.primary.white};
    margin-bottom: ${({ theme }) => theme.layout.gutter};
`;

const Excerpt = styled.div`
    ${h5};
    color: ${({ theme }) => theme.colors.primary.white};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const ArrowIcon = styled(Icon)`
    ${setSmallSpacing('margin-top')};
`;

const Wrapper = styled.article``;

const LinkWrapper = styled(BaseLink)`
    display: block;

    &:hover,
    &:focus {
        ${Subtitle} {
            color: ${({ theme }) => theme.colors.accent.yellow};
        }

        ${Title} {
            color: ${({ theme }) => theme.colors.accent.yellow};
        }

        ${ArrowIcon} {
            svg * {
                color: ${({ theme }) => theme.colors.accent.yellow};
            }
        }
    }
`;

const FeaturedStory = ({
    title,
    subtitle = '',
    excerpt = '',
    featuredImage = {},
    uri = '',
    attributes = DefaultAcfAttributes,
    globalBlockOptions = DefaultGlobalBlockOptions
}) => {
    const ArticleComponent = uri ? LinkWrapper : Wrapper;

    return (
        <BlockSection
            backgroundColor="darkBlue"
            padding="medium"
            {...{ attributes, globalBlockOptions }}
        >
            <IllustrationsBackground backgroundGraphic="articlePageHeader" />
            <Row align="center">
                <Column spanTablet={6} startTablet={1} span={8} start={3}>
                    <FeaturedImageWrapper>
                        {featuredImage?.node ? (
                            <FeaturedImage {...featuredImage.node} />
                        ) : null}
                    </FeaturedImageWrapper>
                </Column>
                <Column spanTablet={6} startTablet={7} span={12} start={1}>
                    <ArticleComponent to={uri}>
                        {subtitle ? (
                            <Subtitle>{formatText(subtitle)}</Subtitle>
                        ) : null}
                        <Title>{formatText(title)}</Title>
                        {excerpt ? (
                            <Excerpt>{formatText(excerpt)}</Excerpt>
                        ) : null}
                        {uri ? (
                            <ArrowIcon
                                icon="circle-arrow"
                                color="yellow"
                                size={82}
                                sizeLarge={82}
                                sizeTablet={40}
                                sizeMobile={40}
                            />
                        ) : null}
                    </ArticleComponent>
                </Column>
            </Row>
        </BlockSection>
    );
};

FeaturedStory.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    excerpt: PropTypes.string,
    featuredImage: PropTypes.shape({}),
    uri: PropTypes.string,
    globalBlockOptions: GlobalBlockOptions,
    attributes: AcfAttributes
};

export default FeaturedStory;
